import { initAssistantI18next } from "@domains/assistant/client/i18n/i18next.ts";
import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { initRoutinesI18next } from "@domains/routines/client/i18n/i18next.ts";
import { AdjustmentsHorizontalIcon, BoltIcon, DocumentTextIcon, PuzzlePieceIcon, SwatchIcon, UserCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import type { AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const { getRequestHandlers } = await import("./api/mocks/getRequestHandlers.ts");

        runtime.registerRequestHandlers(getRequestHandlers());
    }
}

export const registerAccountSettings: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initAssistantI18next(runtime);
    initConnectedAppsI18next(runtime);
    initRoutinesI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "account-settings", "/account", UserIcon, i18nextInstance);

    const account = area.registerSidebarArea("account");

    const general = account.registerRoute("general", {
        path: "/general",
        landing: true,
        page: "AccountGeneral",
        lazy: async () => {
            const { AccountGeneralPage } = await import("./pages/AccountGeneralPage.tsx");

            return { element: <AccountGeneralPage /> };
        }
    });

    const appearance = account.registerRoute("appearance", {
        path: "/appearance",
        page: "AccountAppearance",
        lazy: async () => {
            const { AppearancePage } = await import("./pages/appearance/AppearancePage.tsx");

            return { element: <AppearancePage /> };
        }
    });

    const agent = area.registerSidebarArea("agent");

    const agentPreferences = agent.registerRoute("agent-preferences", {
        path: "/agent-preferences",
        page: "AccountAgentPreferences",
        lazy: async () => {
            const { AgentPreferencesPage } = await import("./pages/AgentPreferencesPage.tsx");

            return { element: <AgentPreferencesPage /> };
        }
    });

    const skills = agent.registerRoute("skills", {
        path: "/skills",
        page: "AccountAgentSkills",
        lazy: async () => {
            const { SkillsPage } = await import("./pages/skills/list/SkillsPage.tsx");

            return { element: <SkillsPage /> };
        }
    });

    const skillDetail = agent.registerTabsArea("skill-detail", "/skills/:skillSlug", {
        commandMenu: false,
        chrome: async () => (await import("./pages/skills/detail/skillDetailTabsChrome.tsx")).SKILL_DETAIL_TABS_CHROME
    });

    skillDetail.registerRoute("view", {
        index: true,
        page: "AccountAgentSkillDetail",
        lazy: async () => {
            const { SkillDetailPage } = await import("./pages/skills/detail/SkillDetailPage.tsx");

            return { element: <SkillDetailPage /> };
        }
    });

    const newSkill = agent.registerTabsArea("skill-new", "/skills/new", {
        commandMenu: false,
        chrome: async () => ({ useRenderAsTitleOnly: () => true })
    });

    newSkill.registerRoute("view", {
        index: true,
        page: "AccountAgentSkillCreate",
        lazy: async () => {
            const { NewSkillPage } = await import("./pages/skills/new/NewSkillPage.tsx");

            return { element: <NewSkillPage /> };
        }
    });

    const customSkill = agent.registerTabsArea("skill-custom", "/skills/custom/:skillSlug", {
        commandMenu: false,
        chrome: async () => (await import("./pages/skills/detail/skillDetailTabsChrome.tsx")).SKILL_DETAIL_TABS_CHROME
    });

    customSkill.registerRoute("view", {
        index: true,
        page: "AccountAgentSkillCustom",
        lazy: async () => {
            const { CustomSkillPage } = await import("./pages/skills/custom/CustomSkillPage.tsx");

            return { element: <CustomSkillPage /> };
        }
    });

    const routines = agent.registerRoute("routines", {
        path: "/routines",
        page: "AccountAgentRoutines",
        lazy: async () => {
            const { RoutinesPage } = await import("./pages/routines/RoutinesPage.tsx");

            return { element: <RoutinesPage /> };
        }
    });

    const connectedApps = agent.registerTabsArea("connected-apps", "/apps");

    const connectedAppsList = connectedApps.registerRoute("apps", {
        index: true,
        page: "AccountConnectedApps",
        lazy: async () => {
            const { ConnectedAppsPage } = await import("./pages/connected-apps/ConnectedAppsPage.tsx");

            return { element: <ConnectedAppsPage /> };
        }
    });

    const connectedAppsAccess = connectedApps.registerRoute("access", {
        path: "/access",
        page: "AccountConnectedAppsAccess",
        lazy: async () => {
            const { ConnectedAppsAccessPage } = await import("./pages/connected-apps/ConnectedAppsAccessPage.tsx");

            return { element: <ConnectedAppsAccessPage /> };
        }
    });

    connectedAppsList.registerOverlay("detail", {
        segment: ":appSlug",
        page: "AccountConnectedAppDetail",
        lazy: async () => {
            const { ConnectedAppDetailRoute } = await import("./pages/connected-apps/ConnectedAppDetailRoute.tsx");

            return { element: <ConnectedAppDetailRoute /> };
        }
    });

    connectedAppsList.registerOverlay("custom-mcp-detail", {
        segment: "custom-mcp/:definitionId",
        page: "AccountCustomMcpDetail",
        lazy: async () => {
            const { CustomMcpDetailRoute } = await import("./pages/connected-apps/CustomMcpDetailRoute.tsx");

            return { element: <CustomMcpDetailRoute /> };
        }
    });

    general.registerLink({ priority: 300, icon: UserCircleIcon });
    appearance.registerLink({ priority: 290, icon: SwatchIcon });

    area.registerToUserMenu({ priority: 0 });

    return (deferredRuntime: FireflyRuntime) => {
        agentPreferences.registerLink({ priority: 200, icon: AdjustmentsHorizontalIcon });
        skills.registerLink({ priority: 195, icon: DocumentTextIcon });

        if (deferredRuntime.getFeatureFlag("weekly-brief", false)) {
            routines.registerLink({ priority: 193, icon: BoltIcon });
        }

        connectedApps.registerLink({ priority: 190, icon: PuzzlePieceIcon });
        connectedAppsList.registerLink({ priority: 1000, commandMenu: false });
        connectedAppsAccess.registerLink({ priority: 999 });
    };
};
